<template>
    <h5 class="subtitle is-5 mb-5">This table lets you view users, update their permissions, and remove them.</h5>

    <div v-if="isLoading" class="has-text-centered">
        <p class="is-size-5">Loading users...</p>
        <progress class="progress is-small is-primary mt-2" max="100"></progress>
    </div>

    <table v-else class="table is-fullwidth">
        <thead>
            <tr>
                <th>Email</th>
                <th>Role</th>
                <th class="has-text-right">Options</th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="user in users" :key="user.$id">
                <td>{{ user.email }}</td>
                <td>
                    <div class="select is-fullwidth">
                        <select :value="user.label" @change="e => handleLabelChange(user.id, e.target.value)">
                            <option value="admin">Admin</option>
                            <option value="editor">Editor</option>
                            <option value="">None</option>
                        </select>
                    </div>
                    <p v-if="savingLabel[user.id]" class="help is-info">Saving...</p>
                    <p v-if="labelError[user.id]" class="help is-danger">{{ labelError[user.id] }}</p>
                </td>
                <td class="has-text-right">
                    <button class="button is-danger" @click="handleDeleteUser(user.id)">Delete</button>
                    <p v-if="deletingUser[user.id]" class="help is-info">Deleting...</p>
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useUsers } from '@/lib/users';

export default {
    setup() {
        const { users, setUserLabel, deleteUser, fetchUserLabels, error } = useUsers();
        const savingLabel = ref({});
        const labelError = ref({});
        const deletingUser = ref({});
        const isLoading = ref(true);

        const handleLabelChange = async (userId, newLabel) => {
            savingLabel.value[userId] = true;
            labelError.value[userId] = null;
            try {
                await setUserLabel(userId, newLabel);
            } catch (err) {
                labelError.value[userId] = err.message || 'An error occurred while saving the label';
            } finally {
                savingLabel.value[userId] = false;
            }
        };

        const handleDeleteUser = async (userId) => {
            deletingUser.value[userId] = true;
            try {
                await deleteUser(userId);
                // Remove the user from the users array
                const userIndex = users.findIndex(user => user.id === userId);
                if (userIndex !== -1) {
                    users.splice(userIndex, 1);
                }
            } catch (err) {
                console.error('Error deleting user:', err);
                // You might want to show this error to the user in the UI
            } finally {
                deletingUser.value[userId] = false;
            }
        };

        onMounted(async () => {
            try {
                await fetchUserLabels();
            } catch (err) {
                console.error('Error fetching user labels:', err);
            } finally {
                isLoading.value = false;
            }
        });

        return {
            users,
            isLoading,
            savingLabel,
            labelError,
            deletingUser,
            error,
            handleLabelChange,
            handleDeleteUser
        };
    }
};
</script>